import { ChangeEvent, useState } from 'react';
import { Checkbox, Select } from 'antd';
import type { Moment } from 'moment';
import moment from 'utils/moment';
import { ANYONE_ID } from 'Calendar/anyone';
import qbSignal from 'QuickBooking/signal';
import serviceAssets from 'QuickBooking/service-images';
import {
  OrderCard,
  OrderHeader,
  OrderTitle,
  Field,
  FieldRow,
  FieldLabel,
  StaffHeader,
  SelectorChip,
  ChipAvatar,
  ServiceChips,
  ServiceChip,
  ServiceAvatar,
  ServiceRemove,
  AddServicesBtn,
  NoteInput,
} from './composer-styles';
import { DurationWrap, DateChip } from './composer-controls';
import AddServicesModal from './AddServicesModal';
import useBookingComposer from './useBookingComposer';
import clockIcon from '../icons/clock.svg';
import chevronDownIcon from '../icons/chevron-down.svg';
import plusIcon from '../icons/plus.svg';
import xRemoveIcon from '../icons/x-remove.svg';
import calendarIcon from '../icons/calendar.svg';
import styled from 'styled-components';

const durationLabel = (v: number) =>
  v < 60 ? `${v} minutes` : v % 60 === 0 ? `${v / 60} hour${v >= 120 ? 's' : ''}` : `${v} minutes`;
const DURATIONS = [15, 30, 45, 60, 90, 120, 150, 180, 240, 300].map((v) => ({
  value: v,
  label: durationLabel(v),
}));

type Props = { composer: ReturnType<typeof useBookingComposer> };

// Functional inline booking composer bound to qbSignal.
const BookingOrderSection = ({ composer }: Props) => {
  const { services, staff, pickStaff, pickTime, setDate, setDuration, setNote, removeService, toggleService } =
    composer;
  const [addOpen, setAddOpen] = useState(false);

  const date = qbSignal.currentDate.value;
  const startTime = qbSignal.startTime.value;
  const duration = qbSignal.duration.value;
  const note = qbSignal.note.value;
  const selected = qbSignal.services.value;
  const requestStaff = qbSignal.requestStaff.value;
  // Calendar range picks can yield any 15' multiple — make sure the current value
  // is always selectable/visible in the duration dropdown.
  const durationOptions = DURATIONS.some((o) => o.value === duration)
    ? DURATIONS
    : [...DURATIONS, { value: duration, label: durationLabel(duration) }].sort((a, b) => a.value - b.value);

  return (
    <OrderCard>
      <OrderHeader>
        <OrderTitle>Booking — Order</OrderTitle>
        <DateChip
          value={date}
          onChange={(d: Moment | null) => setDate(d)}
          format="DD-MM-YYYY"
          allowClear={false}
          inputReadOnly
          placement="bottomRight"
          disabledDate={(c: Moment) => c.isBefore(moment(), 'day')}
          suffixIcon={<img src={calendarIcon} alt="" />}
        />
      </OrderHeader>

      <Field>
        <StaffHeader>
          <FieldLabel>Staff</FieldLabel>
          <CheckboxStyled
            className='request_staff'
            checked={requestStaff}
            onChange={(e: any) => (qbSignal.requestStaff.value = e.target.checked)}
          >
            Request Staff
          </CheckboxStyled>
        </StaffHeader>
        <SelectorChip type="button" onClick={pickStaff}>
          {staff.id !== ANYONE_ID && (
            <ChipAvatar>{staff.avatar && <img src={staff.avatar} alt="" />}</ChipAvatar>
          )}
          {staff.name}
          <img src={chevronDownIcon} alt="" />
        </SelectorChip>
      </Field>

      <FieldRow>
        <Field>
          <FieldLabel>Start time</FieldLabel>
          <SelectorChip type="button" onClick={pickTime}>
            <img src={clockIcon} alt="" />
            {startTime ? startTime : <span className="placeholder">Select time</span>}
            <img src={chevronDownIcon} alt="" />
          </SelectorChip>
        </Field>

        <Field>
          <FieldLabel>Duration</FieldLabel>
          <DurationWrap>
            <Select value={duration} options={durationOptions} onChange={setDuration} />
          </DurationWrap>
        </Field>
      </FieldRow>

      <Field>
        <FieldLabel>Services</FieldLabel>
        {selected.length > 0 && (
          <ServiceChips>
            {selected.map((s) => {
              const image = serviceAssets[String(s.label)];
              return (
                <ServiceChip key={s.value}>
                  <ServiceAvatar>{image && <img src={image} alt="" />}</ServiceAvatar>
                  {s.label}
                  <ServiceRemove type="button" onClick={() => removeService(s.value)}>
                    <img src={xRemoveIcon} alt="remove" />
                  </ServiceRemove>
                </ServiceChip>
              );
            })}
          </ServiceChips>
        )}
        <AddServicesBtn type="button" onClick={() => setAddOpen(true)} disabled={!services.length}>
          <img src={plusIcon} alt="" />
          Add services
        </AddServicesBtn>
        <AddServicesModal
          open={addOpen}
          services={services}
          onClose={() => setAddOpen(false)}
          onToggle={toggleService}
        />
      </Field>

      <Field>
        <FieldLabel>Note</FieldLabel>
        <NoteInput
          value={note}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
          placeholder={'No note — tap "Re Book" to auto-fill'}
        />
      </Field>
    </OrderCard>
  );
};

export default BookingOrderSection;

const CheckboxStyled = styled(Checkbox)`
&.ant-checkbox-wrapper.request_staff {
  align-items: center;
   span {
    font-size: 14px;
   }
}
`;